import { withErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import {
  AskAiCitationNode,
  SuggestedTopicItemNode,
} from "types/graphql-schema";

import ErrorFallback from "@components/error-fallback/error-fallback";
import Loading from "@components/loading/loading";
import { sanitizeMarkdown } from "@helpers/string";

import { parsePartialJson } from "../helpers";
import AskAiArtifact from "./ask-ai-artifact";
import AskAiEmbedExternalEvent from "./ask-ai-embed-external-event";
import AskAIEmbedGoalProgressUpdate from "./ask-ai-embed-goal-progress-update";
import AskAIEmbedSuggestedAssessmentResponses from "./ask-ai-embed-suggested-assessment-responses";
import {
  askAiEmbedContainerClassName,
  askAiEmbedItemClassName,
} from "./ask-ai-embeds";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import AskAIPlaceholderMeeting from "./ask-ai-placeholder-meeting";
import AskAiPlaceholderOverviewDirectReports from "./ask-ai-placeholder-overview-direct-reports";
import AskAiPlaceholderOverviewUser from "./ask-ai-placeholder-overview-user";
import AskAIPlaceholderSuggestedActionItem from "./ask-ai-placeholder-suggested-action-item";
import AskAIPlaceholderSuggestedFeedback from "./ask-ai-placeholder-suggested-feedback";
import AskAIPlaceholderSuggestedGoal from "./ask-ai-placeholder-suggested-goal";
import AskAIPlaceholderSuggestedRecognition from "./ask-ai-placeholder-suggested-recognition";
import AskAiPlaceholderSuggestedTopic from "./ask-ai-placeholder-suggested-topic";

// it's ok if it returns null, we can show a loading state
const getArtifactPlaceholders = (content: any | null): number[] => {
  if (!content) return [];
  if (content.type === "action_item_list") {
    return (content.items || []).map((item: any) => item.id);
  }
  if (content.type === "goal_list") {
    return (content.items || []).map((item: any) => item.id);
  }
  if (content.type === "recognition_list") {
    return (content.items || []).map((item: any) => item.id);
  }
  if (content.type === "feedback_list") {
    return (content.items || []).map((item: any) => item.id);
  }
  return [];
};

const getSuggestedGoalPlaceholder = (
  content: any | null
): { title: string } | null => {
  if (!content?.type || content.type !== "suggested_goal") return null;
  if (!content?.title) return null;
  return {
    title: content.title,
  };
};

const getGoalProgressUpdatesPlaceholder = (
  content: any | null
): {
  items: {
    goalId: number;
    suggestedMessage?: string;
  }[];
} | null => {
  if (
    !content?.type ||
    content.type !== "goal_progress_updates" ||
    !content.items
  )
    return null;
  const items = content.items.map((item: any) => ({
    goalId: item.goal_id,
    suggestedMessage: item.suggested_message,
  }));
  return { items };
};

const getSuggestedActionItemPlaceholder = (
  content: any | null
): { title: string } | null => {
  if (!content?.type || content.type !== "suggested_action_item") return null;
  if (!content?.title) return null;
  return {
    title: content.title,
  };
};

const getSuggestedFeedbackPlaceholder = (
  content: any | null
): { title: string } | null => {
  if (!content?.type || content.type !== "suggested_feedback") return null;
  if (!content?.title) return null;
  return {
    title: content.title,
  };
};

const getSuggestedRecognitionPlaceholder = (
  content: any | null
): { title: string; coreValueId: number | null } | null => {
  if (!content?.type || content.type !== "suggested_recognition") return null;
  if (!content?.title) return null;
  return {
    title: content.title,
    coreValueId: content.core_value_id || null,
  };
};

const getMeetingPlaceholders = (content: any | null): number[] => {
  if (!content || content.type !== "meeting_list") return [];
  return (content.items || []).map((item: any) => item.id);
};

const getSuggestedTopicsPlaceholder = (
  content: any | null
): {
  topics: SuggestedTopicItemNode[];
  meetingId: number | null;
} | null => {
  if (!content || content.type !== "suggested_meeting_topics") return null;
  const meetingId = content.meeting?.id;
  if (!meetingId) return null;
  const topics: SuggestedTopicItemNode[] = (content.topics || []).map(
    (topic: any) =>
      typeof topic === "string" ? { title: topic, notes: null } : topic
  );

  return { topics, meetingId };
};

export type OverviewDirectReportsPlaceholderDirectReportType = {
  user: {
    id: number;
    name: string;
    avatar: string | null;
  };
  summary: string;
};

const getOverviewDirectReportsPlaceholder = (
  content: any | null
): {
  directReports: OverviewDirectReportsPlaceholderDirectReportType[];
} | null => {
  if (!content || content.type !== "overview_direct_reports") return null;
  const directReports = (content.direct_reports || []).map(
    (directReport: any) => ({
      user: {
        id: directReport?.id,
        name: directReport?.name,
        avatar: directReport?.avatar,
      },
      summary: directReport.summary || "",
    })
  ) as OverviewDirectReportsPlaceholderDirectReportType[];
  return { directReports };
};

export type OverviewUserPlaceholderType = {
  goalProgress: string;
  actionItemProgress: string;
  feedbackUpdates: string;
  recognitionsUpdates: string;
  meetingUpdates: string;
  user: {
    id: number;
    name: string;
    avatar: string | null;
  } | null;
};

const getExternalEventsPlaceholder = (content: any | null): any[] => {
  if (!content || content.type !== "external_events" || !content.items)
    return [];
  return content.items.map(({ event_type, event_id, event_data }: any) => {
    try {
      const eventData = parsePartialJson(event_data);
      return { event_type, event_id, event_data: eventData };
    } catch (error) {
      return { event_type, event_id, event_data: {} };
    }
  });
};

const getOverviewUserPlaceholder = (
  content: any | null
): OverviewUserPlaceholderType | null => {
  if (!content || content.type !== "overview_user") return null;
  return {
    goalProgress: content?.goal_progress || "",
    actionItemProgress: content?.action_item_progress || "",
    feedbackUpdates: content?.feedback_updates || "",
    recognitionsUpdates: content?.recognitions_updates || "",
    meetingUpdates: content?.meeting_updates || "",
    user: content
      ? {
          id: content.id,
          name: content.name,
          avatar: content.avatar,
        }
      : null,
  };
};

export type SuggestedAssessmentResponsesPlaceholderType = {
  responses: {
    suggestedResponse: string;
    citations: AskAiCitationNode[];
  }[];
};

const getSuggestedAssessmentResponsesPlaceholder = (
  content: any | null
): SuggestedAssessmentResponsesPlaceholderType | null => {
  if (!content || content.type !== "suggested_assessment_responses")
    return null;
  return {
    responses: compact(content?.items || []).map((item: any) => ({
      suggestedResponse: item?.suggested_response || "",
      citations: (item?.citations || []).map((citation: any) => ({
        title: citation?.source_title || "",
        url: citation?.source_url || "",
      })),
    })),
  };
};

const AskAIPlaceholderOutputItem = ({ content }: { content: any }) => {
  if (!content) return null;

  const artifactPlaceholders = getArtifactPlaceholders(content);
  const externalEventsPlaceholder = getExternalEventsPlaceholder(content);

  const meetingPlaceholders = getMeetingPlaceholders(content);

  const goalProgressUpdatesPlaceholder =
    getGoalProgressUpdatesPlaceholder(content);

  const suggestedRecognitionPlaceholder =
    getSuggestedRecognitionPlaceholder(content);
  const suggestedFeedbackPlaceholder = getSuggestedFeedbackPlaceholder(content);
  const suggestedGoalPlaceholder = getSuggestedGoalPlaceholder(content);
  const suggestedActionItemPlaceholder =
    getSuggestedActionItemPlaceholder(content);

  const suggestedTopicPlaceholder = getSuggestedTopicsPlaceholder(content);

  const overviewDirectReportsPlaceholder =
    getOverviewDirectReportsPlaceholder(content);
  const overviewUserPlaceholder = getOverviewUserPlaceholder(content);
  const suggestedAssessmentResponsesPlaceholder =
    getSuggestedAssessmentResponsesPlaceholder(content);

  return (
    <div className="flex flex-col gap-4">
      {content.explanation && (
        <div>
          <AskAIMessageMarkdown>
            {sanitizeMarkdown(content?.explanation) || ""}
          </AskAIMessageMarkdown>
        </div>
      )}

      {suggestedTopicPlaceholder && (
        <div className={askAiEmbedContainerClassName}>
          <AskAiPlaceholderSuggestedTopic
            meetingId={suggestedTopicPlaceholder.meetingId}
            topics={suggestedTopicPlaceholder?.topics}
            key={suggestedTopicPlaceholder.meetingId}
          />
        </div>
      )}

      {overviewDirectReportsPlaceholder && (
        <AskAiPlaceholderOverviewDirectReports
          overviewDirectReports={overviewDirectReportsPlaceholder}
        />
      )}

      {overviewUserPlaceholder && (
        <AskAiPlaceholderOverviewUser overviewUser={overviewUserPlaceholder} />
      )}

      {meetingPlaceholders.length > 0 && (
        <div className={askAiEmbedContainerClassName}>
          {meetingPlaceholders.map((meetingId) => (
            <div className={askAiEmbedItemClassName} key={meetingId}>
              <AskAIPlaceholderMeeting id={meetingId} />
            </div>
          ))}
          <div className={askAiEmbedItemClassName}>
            <div className="flex justify-center items-center">
              <Loading mini size="4" />
            </div>
          </div>
        </div>
      )}

      {artifactPlaceholders.length > 0 && (
        <div className={askAiEmbedContainerClassName}>
          {artifactPlaceholders.map((artifactId) => (
            <div className={askAiEmbedItemClassName} key={artifactId}>
              <AskAiArtifact id={artifactId} />
            </div>
          ))}
          <div className={askAiEmbedItemClassName}>
            <div className="flex justify-center items-center">
              <Loading mini size="4" />
            </div>
          </div>
        </div>
      )}

      {suggestedRecognitionPlaceholder?.title && (
        <div className={askAiEmbedContainerClassName}>
          <AskAIPlaceholderSuggestedRecognition
            title={suggestedRecognitionPlaceholder.title}
            coreValueId={suggestedRecognitionPlaceholder.coreValueId}
          />
        </div>
      )}

      {suggestedFeedbackPlaceholder && suggestedFeedbackPlaceholder.title && (
        <div className={askAiEmbedContainerClassName}>
          <AskAIPlaceholderSuggestedFeedback
            title={suggestedFeedbackPlaceholder.title}
          />
        </div>
      )}

      {suggestedGoalPlaceholder && suggestedGoalPlaceholder.title && (
        <div className={askAiEmbedContainerClassName}>
          <AskAIPlaceholderSuggestedGoal
            title={suggestedGoalPlaceholder.title}
          />
        </div>
      )}

      {goalProgressUpdatesPlaceholder &&
        goalProgressUpdatesPlaceholder.items.length > 0 && (
          <div>
            {goalProgressUpdatesPlaceholder.items.map((item) => (
              <div className={askAiEmbedContainerClassName} key={item.goalId}>
                <div className={askAiEmbedItemClassName}>
                  <AskAIEmbedGoalProgressUpdate
                    goalId={item.goalId}
                    suggestedMessage={item.suggestedMessage}
                    showCheckinButton={false}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      {suggestedActionItemPlaceholder &&
        suggestedActionItemPlaceholder.title && (
          <div className={askAiEmbedContainerClassName}>
            <AskAIPlaceholderSuggestedActionItem
              title={suggestedActionItemPlaceholder.title}
            />
          </div>
        )}
      {externalEventsPlaceholder.length > 0 && (
        <div className={askAiEmbedContainerClassName}>
          {externalEventsPlaceholder.map((event) => (
            <div
              className={askAiEmbedItemClassName}
              key={`${event.event_type}-${event.event_id}`}
            >
              <AskAiEmbedExternalEvent
                eventType={event.event_type || ""}
                eventData={event.event_data}
              />
            </div>
          ))}
        </div>
      )}
      {suggestedAssessmentResponsesPlaceholder?.responses &&
        suggestedAssessmentResponsesPlaceholder.responses.length > 0 && (
          <div>
            <AskAIEmbedSuggestedAssessmentResponses
              suggestedAssessmentResponses={
                suggestedAssessmentResponsesPlaceholder.responses
              }
            />
          </div>
        )}
    </div>
  );
};

const AskAIPlaceholders = ({
  content,
}: {
  content: {
    output_items: any[];
  };
}) => {
  if (!content) return null;
  const outputItems = compact(content.output_items);

  return (
    <div className="flex flex-col gap-4">
      {outputItems.map((outputItem, index) => (
        <AskAIPlaceholderOutputItem
          key={outputItem?.type || index}
          content={outputItem}
        />
      ))}
    </div>
  );
};

export default withErrorBoundary(AskAIPlaceholders, {
  fallback: ErrorFallback,
});
